import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Card,
  Col,
  DatePicker,
  Divider,
  Empty,
  Row,
  Spin,
  Statistic,
  Table,
  Typography,
} from "antd";
import axios from "axios";
import dayjs, { Dayjs } from "dayjs";

import { API_BASE_URL } from "shared/api/config";
import "./Report.css";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

type CountBySize = Record<string, number>;

interface SummaryResponse {
  date?: string;
  startDate?: string;
  endDate?: string;
  totalBookings: number;
  totalPhotos: number;
  totalFrames: number;
  canvas: number;
  breakdown: {
    giftPhotosBySize: CountBySize;
    additionalPhotosBySize: CountBySize;
    frameOnlyBySize: CountBySize;
    frameAndPaperBySize: CountBySize;
    canvasBySize: CountBySize;
  };
  amount: {
    giftPhotos: number;
    additionalPhotos: number;
    frameOnly: number;
    frameAndPaper: number;
    canvas: number;
    total: number;
    prePay: number;
    postPay: number;
  };
  paymentTypes: Record<"Cash" | "Card" | "Account", number>;
}

const formatMoney = (value?: number | string) =>
  `${Number(value || 0).toLocaleString()}₮`;

const toRows = (data: CountBySize = {}) =>
  Object.entries(data)
    .sort(([first], [second]) => first.localeCompare(second))
    .map(([size, count]) => ({
      key: size,
      size,
      count,
    }));

const SummaryReport: React.FC = () => {
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([
    dayjs(),
    dayjs(),
  ]);
  const [summary, setSummary] = useState<SummaryResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [startDate, endDate] = dateRange;
  const selectedRangeText =
    startDate.isSame(endDate, "day")
      ? startDate.format("YYYY-MM-DD")
      : `${startDate.format("YYYY-MM-DD")} - ${endDate.format("YYYY-MM-DD")}`;

  useEffect(() => {
    const fetchSummary = async () => {
      setLoading(true);
      setError(null);

      try {
        const start = startDate.format("YYYY-MM-DD");
        const end = endDate.format("YYYY-MM-DD");
        const params = new URLSearchParams(
          start === end
            ? { date: start, startDate: start, endDate: end }
            : { startDate: start, endDate: end }
        );
        const res = await axios.get<SummaryResponse>(
          `${API_BASE_URL}/book/daily-summary?${params.toString()}`
        );
        setSummary(res.data);
      } catch (err: any) {
        setError(err.response?.data?.message || "Өдрийн тайлан татахад алдаа гарлаа");
      } finally {
        setLoading(false);
      }
    };

    fetchSummary();
  }, [startDate, endDate]);

  const salesRows = useMemo(
    () =>
      summary
        ? [
            {
              key: "giftPhotos",
              name: "Дагалдах зураг",
              count: summary.totalPhotos,
              amount: summary.amount.giftPhotos,
            },
            {
              key: "additionalPhotos",
              name: "Нэмэлт зураг",
              count: Object.values(summary.breakdown.additionalPhotosBySize || {}).reduce(
                (sum, count) => sum + count,
                0
              ),
              amount: summary.amount.additionalPhotos,
            },
            {
              key: "frameOnly",
              name: "Дан жааз",
              count: Object.values(summary.breakdown.frameOnlyBySize || {}).reduce(
                (sum, count) => sum + count,
                0
              ),
              amount: summary.amount.frameOnly,
            },
            {
              key: "frameAndPaper",
              name: "Жааз + зураг",
              count: Object.values(summary.breakdown.frameAndPaperBySize || {}).reduce(
                (sum, count) => sum + count,
                0
              ),
              amount: summary.amount.frameAndPaper,
            },
            {
              key: "canvas",
              name: "Canvas",
              count: summary.canvas,
              amount: summary.amount.canvas,
            },
          ]
        : [],
    [summary]
  );

  const countColumns = [
    { title: "Хэмжээ", dataIndex: "size", key: "size" },
    { title: "Тоо", dataIndex: "count", key: "count", align: "right" as const },
  ];

  const renderBreakdownTable = (title: string, rows: ReturnType<typeof toRows>) => (
    <Card title={title} className="report-card">
      {rows.length ? (
        <Table
          columns={countColumns}
          dataSource={rows}
          pagination={false}
          size="small"
        />
      ) : (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Мэдээлэл алга" />
      )}
    </Card>
  );

  return (
    <div className="report-page">
      <div className="report-toolbar">
        <div>
          <Title level={3}>Өдрийн тайлан</Title>
          <Text type="secondary">
            Төлбөр, зураг, жааз, canvas-ийн сонгосон хугацааны нэгтгэл
          </Text>
        </div>
        <RangePicker
          value={dateRange}
          onChange={(dates) => {
            if (dates?.[0] && dates?.[1]) {
              setDateRange([dates[0], dates[1]]);
            }
          }}
          allowClear={false}
        />
      </div>

      <Text type="secondary">Хугацаа: {selectedRangeText}</Text>

      {error && <Alert message={error} type="error" showIcon />}
      {loading && <Spin />}

      {!loading && summary && (
        <>
          <Row gutter={[16, 16]} className="report-stat-grid">
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic title="Бүртгэл" value={summary.totalBookings} />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic title="Урьдчилгаа" value={summary.amount.prePay} formatter={formatMoney} />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic title="Үлдэгдэл" value={summary.amount.postPay} formatter={formatMoney} />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic title="Нийт төлбөр" value={summary.amount.total} formatter={formatMoney} />
              </Card>
            </Col>
          </Row>

          <Row gutter={[16, 16]} className="report-stat-grid">
            <Col xs={24} md={8}>
              <Card>
                <Statistic title="Бэлэн" value={summary.paymentTypes.Cash} formatter={formatMoney} />
              </Card>
            </Col>
            <Col xs={24} md={8}>
              <Card>
                <Statistic title="Карт" value={summary.paymentTypes.Card} formatter={formatMoney} />
              </Card>
            </Col>
            <Col xs={24} md={8}>
              <Card>
                <Statistic title="Данс" value={summary.paymentTypes.Account} formatter={formatMoney} />
              </Card>
            </Col>
          </Row>

          <Divider />

          <Card title="Борлуулалтын нэгтгэл" className="report-card">
            <Table
              columns={[
                { title: "Төрөл", dataIndex: "name", key: "name" },
                { title: "Тоо", dataIndex: "count", key: "count", align: "right" as const },
                {
                  title: "Дүн",
                  dataIndex: "amount",
                  key: "amount",
                  align: "right" as const,
                  render: formatMoney,
                },
              ]}
              dataSource={salesRows}
              pagination={false}
              size="small"
            />
          </Card>

          <div className="report-breakdown-grid">
            {renderBreakdownTable("Canvas", toRows(summary.breakdown.canvasBySize))}
            {renderBreakdownTable("Дан жааз", toRows(summary.breakdown.frameOnlyBySize))}
            {renderBreakdownTable("Жааз + зураг", toRows(summary.breakdown.frameAndPaperBySize))}
            {renderBreakdownTable("Дагалдах зураг", toRows(summary.breakdown.giftPhotosBySize))}
            {renderBreakdownTable("Нэмэлт зураг", toRows(summary.breakdown.additionalPhotosBySize))}
          </div>
        </>
      )}
    </div>
  );
};

export default SummaryReport;
